import {
  convertDateToYYYYMMDDFormat,
  FormFeedBack,
  NoDataComponent,
  PageWrapper,
  STATUSPAGE,
  TableVirtualized,
  TableWithGroupingsLoadingSkeleton,
} from '@repo/ui';
import { HandleRejectedByProfessionalsUseCaseInterface } from 'domain/usecases';
import { TYPE_REPORT } from 'domain/value-objects/type-report';
import { useFormik } from 'formik';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useRejectedByProfessionals } from 'presentation/app/modules/Reports/hooks/useRejectedByProfessionals';
import { Col, Row, Card, Input, Label, Button, Spinner } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';

import { makeActivitiesRejectedByProfessionals } from '../../../common/columns/activities/activities-rejected-by-professionals-columns';

export const RejectedByProfessionals = () => {
  function getFirstDayOfMonth() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  function getCurrentDate() {
    return new Date();
  }

  const {
    loading,
    hasFetchedData,
    selectedSubmitBtn,
    rejectedByProfessionals,
    setSelectedSubmitBtn,
    requestHandleRejectedByProfessionals,
    requestHandleRejectedByProfessionalsJson,
  } = useRejectedByProfessionals();

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      unit: null,
      groups: [],
      entity: null,
      course: null,
      semester: null,
      ends: convertDateToYYYYMMDDFormat(getCurrentDate()),
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Campo é obrigatório!'),
      ends: Yup.date().required('Campo é obrigatório!'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit: Omit<
        HandleRejectedByProfessionalsUseCaseInterface.Params,
        'type'
      > = {
        entity: values.entity?.value,
        course: values.course?.value,
        semester: values.semester?.value,
        curricularUnit: values.unit?.value,
        groups: values.groups.map((group) => group.value),
        ends: values.ends,
        starts: values.starts,
      };

      switch (selectedSubmitBtn) {
        case 'export-pdf':
          requestHandleRejectedByProfessionals({
            ...valuesToSubmit,
            type: TYPE_REPORT.PDF,
          });
          break;
        case 'export-excel':
          requestHandleRejectedByProfessionals({
            ...valuesToSubmit,
            type: TYPE_REPORT.EXCEL,
          });
          break;
        case 'export-json':
          requestHandleRejectedByProfessionalsJson({
            ...valuesToSubmit,
            type: TYPE_REPORT.JSON,
          });
          break;
        default:
          break;
      }
    },
  });

  const formattedData = rejectedByProfessionals?.reduce(
    (acc, professional) => [
      ...acc,
      {
        id: uuid(),
        isMacroHeader: true,
        title: (
          <div className="d-flex gap-3 align-items-center">
            <span>{professional.professional}</span>
            <span className="fs-6">
              Total de atividades do Profissional: {professional.total}
            </span>
          </div>
        ),
      },
      ...professional.groups.flatMap((group) => [
        {
          id: uuid(),
          isHeader: true,
          title: (
            <div className="d-flex justify-content-between">
              <span>{group.group}</span>{' '}
              <span>Total de atividades do Grupo: {group.total}</span>
            </div>
          ),
        },
        ...group.activities.map((activity) => ({
          id: uuid(),
          ...activity,
        })),
      ]),
    ],
    [],
  );

  return (
    <PageWrapper title={`Atividades Rejeitadas por Profissionais`}>
      <h4 className="mb-4">Atividades Rejeitadas por Profissionais</h4>
      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Col>
                <Row>
                  <Col lg={6} className="mb-3">
                    <Label htmlFor="initial-date" className="form-label">
                      Data inicial:
                    </Label>
                    <Input
                      type="date"
                      id="starts"
                      name="starts"
                      className="form-control"
                      value={validation.values.starts}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(validation.touched && validation.errors?.starts)
                      }
                    />
                    <FormFeedBack
                      error={validation.errors?.starts}
                      isInvalid={
                        !!(validation.touched && validation.errors?.starts)
                      }
                    />
                  </Col>

                  <Col lg={6} className="mb-3">
                    <Label htmlFor="final-date" className="form-label">
                      Data final:
                    </Label>
                    <Input
                      type="date"
                      id="ends"
                      name="ends"
                      className="form-control"
                      value={validation.values.ends}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(validation.touched && validation.errors?.ends)
                      }
                    />
                    <FormFeedBack
                      error={validation.errors?.ends}
                      isInvalid={
                        !!(validation.touched && validation.errors?.ends)
                      }
                    />
                  </Col>
                </Row>
              </Col>
            </>
          }
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end">
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-json');
                }}
                color="primary"
                type="submit"
                id="export-json"
                disabled={Boolean(loading)}
              >
                {selectedSubmitBtn === 'export-json' && Boolean(loading) ? (
                  <Spinner size="sm">Carregando...'</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-pdf');
                }}
                color="primary"
                type="submit"
                className="btn btn-light waves-effect waves-light"
                id={'save_button'}
                disabled={Boolean(loading)}
              >
                {selectedSubmitBtn === 'export-pdf' && Boolean(loading) ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Exportar para PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-excel');
                }}
                color="primary"
                type="submit"
                id={'save_button'}
                className="btn btn-light waves-effect waves-light"
                disabled={Boolean(loading)}
              >
                {selectedSubmitBtn === 'export-excel' && Boolean(loading) ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ms-2 ri-file-excel-2-line"></i>
                  </>
                )}
              </Button>
            </div>
          }
        />
        {rejectedByProfessionals?.length === 0 &&
          hasFetchedData &&
          loading === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent />
            </div>
          )}

        {loading === STATUSPAGE.LOADING && (
          <TableWithGroupingsLoadingSkeleton />
        )}

        {loading === STATUSPAGE.DONE && rejectedByProfessionals?.length > 0 && (
          <TableVirtualized
            columns={makeActivitiesRejectedByProfessionals()}
            data={formattedData}
          />
        )}
      </Card>
    </PageWrapper>
  );
};
